import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useRokuContactOnCallLinks, rkpEvent } from "../lib/rkp";
import { trackLeadSubmit, usePhoneTapTracking } from "../analytics";
import { Phone, HandHeart, Car, ShoppingBag, Sparkle, ForkKnife, TShirt, Pill, CheckCircle, List, X, MapPin, Clock, ShieldCheck } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGES = [
  { src: "/shc-gen-errands.webp", alt: "A Silver Home Care caregiver in a black polo grocery shopping with a client" },
  { src: "/shc-set-walk.webp", alt: "A Silver Home Care caregiver pushing a client in his wheelchair past brick ranch homes" },
  { src: "/shc-set-garden.webp", alt: "A Silver Home Care caregiver potting geraniums with a client under the carport" },
  { src: "/shc-set-coffee.webp", alt: "A Silver Home Care caregiver laughing with a client over coffee at his kitchen table" },
  { src: "/shc-set-porch.webp", alt: "A Silver Home Care caregiver and a client laughing over coffee on her front porch" },
  { src: "/shc-set-door-coat.webp", alt: "A Silver Home Care caregiver helping a client into his jacket at the front door before a walk" },
  { src: "/shc-set-personal-care.webp", alt: "A Silver Home Care caregiver helping a client button her cardigan in her bedroom" },
];

function PortalLoginLink({ role, to = "/dashboard", className, testid, children, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/preview-login`, { role }, { withCredentials: true });
      window.location.href = to;
      return;
    } catch { /* production: fall through to the sign-in page */ }
    setBusy(false);
    window.location.href = "/login";
    onDone?.();
  };
  return <a href="/login" onClick={go} className={className} data-testid={testid}>{busy ? "Opening…" : children}</a>;
}

function CaregiverPortalLink({ className, testid, children, onDone }) {
  // Shown everywhere, including the public domain — caregivers enter through the site footer (owner 2026-10-02)
  return <PortalLoginLink role="caregiver" className={className} testid={testid} onDone={onDone}>{children}</PortalLoginLink>;
}

const SERVICES = [
  { img: "/shc-set-bathroom.webp", alt: "A Silver Home Care caregiver helping a client wash up at the sink in her bathroom",
    cat: "Daily Living Support", title: "Personal Care & Hygiene",
    p1: "We provide compassionate assistance with daily personal care activities, helping clients maintain their dignity and independence while staying clean, comfortable, and confident.",
    p2: "Our trained caregivers offer respectful support tailored to each individual's needs and preferences.",
    items: [["Bathing & Showering", "Safe assistance with bathing, showering, and hygiene"], ["Dressing", "Help selecting and putting on clothing"], ["Grooming", "Hair care, shaving, oral hygiene, and skincare"], ["Toileting", "Respectful assistance and incontinence care"], ["Mobility Support", "Transfers, positioning, and fall prevention"], ["Eating Assistance", "Help with meals and feeding if needed"]] },
  { img: "/shc-set-companionship.webp", alt: "A Silver Home Care caregiver playing cards with a laughing client at his kitchen table",
    cat: "Social Connection", title: "Companionship & Social Engagement",
    p1: "Loneliness doesn't have to be part of aging. Our companionship services provide meaningful connection, conversation, and shared activities that brighten each day.",
    p2: "Whether it's a friendly face, a listening ear, or a partner for activities, our caregivers create bonds that bring joy and purpose to daily life.",
    items: [["Conversation", "Meaningful dialogue and active listening"], ["Games & Puzzles", "Brain-stimulating activities"], ["Reading Together", "Shared stories and audiobooks"], ["Walks & Exercise", "Fresh air and gentle movement"], ["Hobbies", "Arts, crafts, and creative activities"], ["Community Events", "Outings and social activities"]] },
  { img: "/shc-dishes.webp", pos: "top", alt: "A Silver Home Care caregiver washing dishes while her client enjoys a cup of tea at the table",
    cat: "Home Maintenance", title: "Light Housekeeping",
    p1: "A clean, organized home supports comfort, safety, and peace of mind. Our housekeeping services help maintain a tidy living space without the stress or physical strain.",
    p2: "We handle routine cleaning tasks so you can focus on what matters most—staying healthy, happy, and at home.",
    items: [["Tidying Up", "Organizing living areas and decluttering"], ["Floor Care", "Sweeping, mopping, and vacuuming"], ["Kitchen Cleanup", "Dishes, counters, and appliances"], ["Laundry", "Washing, drying, and folding clothes"], ["Linens", "Changing sheets and making beds"], ["Trash & Recycling", "Taking out waste and recycling"]] },
  { img: "/shc-set-meal-prep.webp", pos: "top", alt: "A Silver Home Care caregiver serving a hot dinner plate to a client at his kitchen table",
    cat: "Nutrition Support", title: "Meal Preparation",
    p1: "Good nutrition is essential for health and wellbeing. Our meal preparation services ensure clients enjoy nutritious, delicious meals tailored to their dietary needs and preferences.",
    p2: "From planning and shopping to cooking and cleanup, we handle every step so mealtimes remain enjoyable and stress-free.",
    items: [["Menu Planning", "Helping them plan, or following the plan they already have"], ["Grocery Shopping", "Purchasing fresh ingredients"], ["Cooking", "Preparing fresh, home-cooked meals"], ["Dietary Restrictions", "Accommodating special needs"], ["Feeding Assistance", "Help during mealtimes if needed"], ["Kitchen Cleanup", "Washing dishes and tidying up"]] },
  { img: "/shc-medication.webp", alt: "A Silver Home Care caregiver presenting a client her own prepared pill box with a glass of water",
    cat: "Daily Routines", title: "Medication Reminders",
    p1: "A timely reminder, their own pill box presented, and something to drink to take it with. The box is prepared by you or them — we just make sure it's never forgotten.",
    items: [["Timely Reminders", "A prompt at the right time"], ["Pill Box Presented", "Prepared by you or them"], ["Something to Drink", "Water or juice to take it with"], ["We Tell You", "If a dose is skipped, you hear about it"]] },
  { img: "/brand-transport.webp", alt: "A Silver Home Care caregiver in a black polo steadying a client into the car on an overcast day",
    cat: "Mobility Support", title: "Transportation Assistance",
    p1: "Getting to appointments, errands, and social activities shouldn't be a barrier to staying active and independent. Our transportation services provide safe, reliable rides when you need them.",
    p2: "From medical appointments to grocery shopping, we help clients maintain their routines and stay connected to their communities.",
    items: [["Medical Appointments", "Doctor visits and treatments"], ["Shopping Trips", "Grocery and personal shopping"], ["Pharmacy Runs", "Prescription pickup"], ["Social Events", "Outings and family visits"], ["Personal Errands", "Banking and appointments"], ["Mobility Accessible", "Wheelchair-friendly vehicles"]] },
  { img: "/shc-set-respite.webp", alt: "A Silver Home Care caregiver and a client laughing over a photo album on the sofa",
    cat: "Family Support", title: "Respite Care for Family Caregivers",
    p1: "Family caregivers need breaks too. Our respite care services provide temporary relief so you can rest, recharge, and tend to your own needs—knowing your loved one is in capable, caring hands.",
    p2: "Whether you need a few hours, a day, or longer-term support, we're here to help you maintain balance and prevent burnout.",
    items: [["Flexible Scheduling", "Hours to weeks of coverage"], ["Regular Breaks", "Scheduled relief periods"], ["Emergency Support", "Last-minute care available"], ["In-Home Care", "Familiar environment"], ["Trained Caregivers", "Professional, experienced staff"], ["Peace of Mind", "Trusted, reliable support"]] },
  { img: "/shc-evv-phone.webp",
    cat: "Around the Clock", title: "24-Hour Awake Care",
    p1: "For clients who can't be left alone — fall risk, wandering, post-hospital recovery — caregivers rotate in shifts around the clock, so someone awake and alert is always in the home. Not live-in care: no one sleeps on duty.",
    items: [["Awake Overnight", "Always alert, never sleeping"], ["Shift Rotations", "Fresh caregivers around the clock"], ["Fall & Wandering Watch", "Constant supervision"], ["Post-Hospital Support", "Safe recovery at home"], ["Dementia Supervision", "Steady, patient presence"], ["Family Updates", "You're always in the loop"]] },
];

function LeadForm() {
  const [form, setForm] = useState({ name: "", phone: "", who_for: "", payer: "", heard_from: "", message: "", free_services: false });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Just your name and phone number is all we need"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/request-care`, form);
      rkpEvent("CONTACT");
      trackLeadSubmit();
      setSent(true);
    } catch { toast.error("Something went wrong — please call us at 800-719-6912"); }
    setBusy(false);
  };

  const inputCls = "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-base text-[#23282f] placeholder:text-stone-400 focus:border-[#6A3E97] focus:outline-none focus:ring-2 focus:ring-[#6A3E97]/20 transition-colors";

  if (sent) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-8 text-center shadow-lg" data-testid="site-request-success">
        <CheckCircle size={40} weight="fill" className="mx-auto text-[#1e8449]" />
        <p className="mt-3 font-heading text-2xl font-medium text-[#23282f]">We'll call you back.</p>
        <p className="mt-2 text-sm text-stone-600">A real person from our office — usually the same business day. Sooner if you need us: <a href="tel:8007196912" className="font-semibold text-[#6A3E97]">800-719-6912</a>.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-lg sm:p-7" data-testid="site-request-form" id="request">
      <p className="font-heading text-2xl font-medium tracking-tight text-[#23282f]">Request a call back</p>
      <p className="mt-1 text-sm text-stone-600">Free, no obligation. Tell us what's going on and we'll help you sort it out.</p>
      <div className="mt-5 space-y-4">
        <input value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} data-testid="site-req-name" />
        <input value={form.phone} onChange={set("phone")} type="tel" placeholder="Phone *" className={inputCls} data-testid="site-req-phone" />
        <select value={form.who_for} onChange={set("who_for")} className={inputCls} data-testid="site-req-who">
          <option value="">Who needs care?</option>
          {["My parent", "My spouse", "Another family member", "Myself", "Someone I help care for"].map((o) => <option key={o}>{o}</option>)}
        </select>
        <select value={form.payer} onChange={set("payer")} className={inputCls} data-testid="site-req-payer">
          <option value="">How would care be paid for?</option>
          {["Community HealthChoices / Medicaid", "Private pay", "Long-term care insurance", "Not sure — help me figure it out"].map((o) => <option key={o}>{o}</option>)}
        </select>
        <select value={form.heard_from} onChange={set("heard_from")} className={inputCls} data-testid="site-req-heard">
          <option value="">How did you hear about us? (optional)</option>
          {["TV / Roku ad", "Facebook or Instagram", "Google search", "A friend or family member", "A nurse, doctor, or care coordinator", "Other"].map((o) => <option key={o}>{o}</option>)}
        </select>
        <textarea value={form.message} onChange={set("message")} rows={2} placeholder="Anything we should know? (optional)" className={inputCls} data-testid="site-req-message" />
        <label className="flex items-start gap-2.5 text-sm text-stone-600">
          <input type="checkbox" checked={form.free_services} onChange={(e) => setForm((f) => ({ ...f, free_services: e.target.checked }))}
            className="mt-0.5 h-4 w-4 rounded border-stone-300 accent-[#6A3E97]" data-testid="site-req-free-services" />
          I'd like to find out how to apply for free in-home services
        </label>
        <button onClick={submit} disabled={busy}
          className="w-full rounded-md bg-[#6A3E97] px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A] disabled:opacity-50"
          data-testid="contact-form-submit">
          {busy ? "Sending…" : "Request a call back"}
        </button>
        <p className="text-center text-xs text-stone-400">Or call us right now — <a href="tel:8007196912" className="font-semibold text-[#6A3E97]">800-719-6912</a></p>
      </div>
    </div>
  );
}

export default function PublicSitePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [assignedHero, setAssignedHero] = useState(null);
  const [assignedAbout, setAssignedAbout] = useState(null);
  const [aboutIdx, setAboutIdx] = useState(0);
  const [serviceImages, setServiceImages] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/public/image-assignments`).then(({ data }) => {
      const slots = data?.slots || {};
      if (slots["homepage-hero"]?.length) setAssignedHero(slots["homepage-hero"].map((n) => ({ src: `/${n}`, alt: "Silver Home Care caregiver with a client" })));
      if (slots["homepage-about"]?.length) setAssignedAbout(slots["homepage-about"]);
      const svc = {};
      for (const [slot, names] of Object.entries(slots)) {
        if (slot.startsWith("service-") && names.length) svc[slot] = `/${names[0]}`;
      }
      setServiceImages(svc);
    }).catch(() => {});
  }, []);
  const aboutImages = assignedAbout || ["/scene-manicure.webp"];
  useEffect(() => {
    if (aboutImages.length < 2) return undefined;
    const t = setInterval(() => setAboutIdx((i) => (i + 1) % aboutImages.length), 6000);
    return () => clearInterval(t);
  }, [aboutImages.length]);
  const heroImages = assignedHero || HERO_IMAGES;
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, [heroImages.length]);
  useRokuContactOnCallLinks();
  usePhoneTapTracking();

  return (
    <div className="min-h-screen scroll-smooth bg-white text-[#23282f]" data-testid="public-site">
      {/* Name-disambiguation banner */}
      <div className="bg-[#2E1A3E] px-4 py-4 text-center text-sm leading-relaxed text-white/90 sm:py-5 sm:text-base" data-testid="identity-banner">
        This is Silver Home Care of <span className="font-bold text-white">Fairless Hills, Pennsylvania</span> — we are not affiliated with
        Silver Home Care agencies in Connecticut or Michigan. Looking for them? Visit{" "}
        <a href="https://silverhomecare.org" target="_blank" rel="noreferrer" className="font-bold text-white underline underline-offset-2 hover:text-white/80">silverhomecare.org</a>.
      </div>
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-stone-200 bg-white" data-testid="site-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" />
          <nav className="hidden items-center gap-7 text-sm font-medium text-stone-600 md:flex">
            <a href="#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="#contact" className="transition-colors hover:text-[#6A3E97]">Contact us</a>
            <a href="https://search.google.com/local/writereview?placeid=ChIJezAnVXBVwYkREnwdgohqzGM" target="_blank" rel="noreferrer" className="transition-colors hover:text-[#6A3E97]" data-testid="site-nav-review">Leave us a review</a>
            <Link to="/apply" className="transition-colors hover:text-[#6A3E97]" data-testid="site-nav-apply">Caregivers apply</Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="tel:8007196912" className="inline-flex items-center gap-1.5 rounded-md bg-[#6A3E97] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="site-call-cta">
              <Phone size={14} weight="fill" /> 800-719-6912
            </a>
            <button className="text-[#23282f] md:hidden" onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="site-menu-open"><List size={26} /></button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white p-6" data-testid="site-mobile-menu">
          <div className="flex items-center justify-between">
            <img src="/shc-logo.png" alt="" className="h-10 w-auto" />
            <button onClick={() => setMenuOpen(false)} aria-label="Close" data-testid="site-menu-close"><X size={26} /></button>
          </div>
          <nav className="mt-10 flex flex-col gap-6 text-3xl font-semibold tracking-tight text-[#23282f]">
            <a href="#services" onClick={() => setMenuOpen(false)}>Services</a>
            <a href="#about" onClick={() => setMenuOpen(false)}>About us</a>
            <a href="#contact" onClick={() => setMenuOpen(false)}>Contact us</a>
            <a href="https://search.google.com/local/writereview?placeid=ChIJezAnVXBVwYkREnwdgohqzGM" target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>Leave us a review</a>
            <Link to="/apply" onClick={() => setMenuOpen(false)}>Caregivers apply</Link>
          </nav>
        </div>
      )}

      {/* Hero */}
      <section className="bg-[#F5F2FA]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:py-14">
          <div className="lg:col-span-6">
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Non-medical home care · Bucks &amp; Philadelphia Counties</p>
            <h1 className="mt-3 font-heading text-4xl font-medium leading-[1.1] tracking-tight text-[#23282f] sm:text-5xl" data-testid="site-hero-headline">
              The right help at home changes everything.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
              The real value of home care isn't any single visit. It's dependable help, visit after visit,
              from people who learn the routines, the preferences, and what a good day looks like in your home.
              Your caregiver is matched to the person, not just the schedule — and backed by management that
              stays involved and responsive.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href="#contact" className="rounded-md bg-[#6A3E97] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="hero-request-btn">
                Request care
              </a>
              <a href="tel:8007196912" className="inline-flex items-center gap-2 text-base font-semibold text-[#23282f] hover:text-[#6A3E97]" data-testid="hero-call">
                <Phone size={18} weight="fill" className="text-[#6A3E97]" /> 800-719-6912
              </a>
            </div>
            <p className="mt-6 max-w-xl border-l-2 border-[#6A3E97] pl-4 text-sm leading-relaxed text-stone-600">
              Worried about paying for care? You may be eligible for free services through Pennsylvania's
              Community HealthChoices program. And if a family member is already providing the care,
              they may be able to get paid for it.{" "}
              <a href="tel:8007196912" className="font-semibold text-[#6A3E97] hover:underline" data-testid="site-free-care-call">Give us a call</a>{", "}
              or <a href="#contact" className="font-semibold text-[#6A3E97] hover:underline" data-testid="site-free-care-link">fill out our form</a>{" "}
              — ask us how to apply.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="relative" data-testid="site-hero-carousel">
              {heroImages.map((im, i) => (
                <img key={im.src} src={im.src} alt={im.alt} data-testid={i === heroIdx ? "site-hero-img" : undefined}
                  className={`max-h-[520px] w-full rounded-t-full rounded-b-xl object-cover shadow-lg transition-opacity duration-700 ${i === heroIdx ? "relative opacity-100" : "absolute inset-0 h-full opacity-0"}`} />
              ))}
              <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/25 px-2.5 py-1.5 backdrop-blur-sm">
                {heroImages.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)} aria-label={`Show photo ${i + 1}`} data-testid={`hero-dot-${i}`}
                    className={`h-2 w-2 rounded-full transition-colors ${i === heroIdx ? "bg-white" : "bg-white/50 hover:bg-white/80"}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-stone-100 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 sm:grid-cols-3 sm:px-6 lg:px-10" data-testid="site-trust">
          {[
            { icon: ShieldCheck, title: "Licensed & local since 2016", text: "A Pennsylvania-licensed, non-medical home care agency — locally owned and hands-on, ten years in Bucks County." },
            { icon: HandHeart, title: "Matched, not just scheduled", text: "Your caregiver is matched to the person and introduced face to face before the first visit — so day one already feels familiar." },
            { icon: Clock, title: "Management that stays involved", text: "When you call, a person picks up. And the office stays involved and responsive." },
          ].map((t) => (
            <div key={t.title} className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#6A3E97]/10">
                <t.icon size={22} weight="duotone" className="text-[#6A3E97]" />
              </span>
              <div>
                <p className="font-heading text-lg font-medium tracking-tight text-[#23282f]">{t.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-[#F5F2FA]">
        <div className="mx-auto max-w-7xl scroll-mt-20 px-4 py-10 sm:px-6 lg:px-10 lg:py-14" data-testid="site-services">
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Services</p>
          <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">What we help with</h2>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-stone-600">
            Non-medical, everyday help — the things that keep a household running and a person independent.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s) => (
              <div key={s.title} className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm"
                data-testid={`site-service-${s.title.toLowerCase().replace(/\W+/g, "-")}`}>
                <img src={serviceImages[`service-${s.title.toLowerCase().replace(/\W+/g, "-")}`] || s.img} alt={s.alt || s.title} loading="lazy"
                  className={`h-36 w-full object-cover ${s.pos === "top" ? "object-top" : ""}`} />
                <div className="p-5">
                  <h3 className="font-heading text-xl font-medium tracking-tight text-[#23282f]">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{s.p1}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#contact" className="rounded-md bg-[#6A3E97] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#55317A]" data-testid="services-start-cta">Get started</a>
            <a href="tel:8007196912" className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-6 py-3 text-base font-semibold text-[#23282f] transition-colors hover:border-[#6A3E97] hover:text-[#6A3E97]" data-testid="services-call-cta">
              <Phone size={16} weight="fill" /> 800-719-6912
            </a>
          </div>
        </div>
      </section>

      {/* About us */}
      <section id="about" className="border-y border-stone-100 bg-white">
        <div className="mx-auto grid max-w-7xl scroll-mt-20 grid-cols-1 items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-10 lg:py-14" data-testid="site-about">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">About us</p>
            <h2 className="mt-3 font-heading text-3xl font-medium leading-tight tracking-tight text-[#23282f] sm:text-4xl">
              About Silver Home Care
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
              Silver Home Care is a locally owned, non-medical home care agency based in Fairless
              Hills, PA, serving elderly and disabled residents across Bucks and Philadelphia Counties.
            </p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-stone-600">
              Since 2016, we've been built on a simple idea: reliable care starts with a reliable
              team. That belief guides how we support caregivers, communicate with families, and
              stay involved in the care we provide.
            </p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-stone-600">
              We're proud to be a Community HealthChoices provider, contracted with all three MCOs:
              Keystone First Community HealthChoices, UPMC Community HealthChoices, and
              PA Health &amp; Wellness.{" "}
              <a href="tel:8007196912" className="font-semibold text-[#6A3E97] hover:underline" data-testid="about-chc-call">Give us a call</a>{", "}
              or <a href="#contact" className="font-semibold text-[#6A3E97] hover:underline" data-testid="about-chc-link">fill out the form below</a>{" "}
              — ask us how to apply for free services.
            </p>
          </div>
          <div className="relative" data-testid="site-about-carousel">
            {aboutImages.map((src, i) => (
              <img key={src} src={src} alt="A Silver Home Care caregiver with a client at home"
                data-testid={i === aboutIdx ? "site-about-img" : undefined}
                className={`max-h-[380px] w-full rounded-xl object-cover shadow-lg transition-opacity duration-700 ${i === aboutIdx ? "relative opacity-100" : "absolute inset-0 h-full opacity-0"}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact / request care */}
      <section id="contact" className="bg-[#F5F2FA]">
        <div className="mx-auto grid max-w-7xl scroll-mt-20 grid-cols-1 gap-10 px-4 py-10 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:px-10 lg:py-14" data-testid="site-request">
          <div className="lg:col-span-6">
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Contact us</p>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">Talk to a person.</h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-stone-600">
              Whether you're sure you need care or just starting to look — give us a call or fill out the form and we'll follow up.
            </p>
            <div className="mt-8 space-y-5">
              <a href="tel:8007196912" className="flex items-center gap-3" data-testid="site-contact-phone">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><Phone size={20} weight="fill" className="text-[#6A3E97]" /></span>
                <span><span className="block text-2xl font-semibold tracking-tight text-[#23282f]">800-719-6912</span>
                <span className="text-sm text-stone-500">Mon–Fri 9–5 for routine matters · on-shift support & scheduling 24/7</span></span>
              </a>
              <p className="pl-14 text-xs text-stone-500" data-testid="contact-non-affiliation">Not affiliated with Silver Home Care agencies in Michigan or Connecticut.</p>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><MapPin size={20} weight="duotone" className="text-[#6A3E97]" /></span>
                <span><span className="block font-semibold text-[#23282f]">680 Lincoln Hwy, Fairless Hills, PA 19030</span>
                <span className="text-sm text-stone-500">Office hours Monday–Friday, 9–5 · stop-ins by appointment</span></span>
              </div>
              <div className="flex items-center gap-3" data-testid="site-free-care-link-2">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><CheckCircle size={20} weight="fill" className="text-[#6A3E97]" /></span>
                <span><span className="block font-semibold text-[#23282f]">You may be eligible for free care</span>
                <span className="text-sm text-stone-500">Fill out the form or give us a call — ask us how to apply.</span></span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <LeadForm />
          </div>
        </div>
        <div className="border-t border-stone-200 bg-white">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-10" data-testid="site-footer">
            <p>© 2026 Silver Home Care · Equal opportunity employer and provider · Non-medical services under a care plan agreed with each family</p>
            <div className="flex items-center gap-5">
              <Link to="/apply" className="text-xs font-medium text-stone-500 transition-colors hover:text-[#6A3E97]" data-testid="site-footer-apply">Caregivers apply</Link>
              <Link to="/client-handbook" className="text-xs font-medium text-stone-500 transition-colors hover:text-[#6A3E97]" data-testid="site-footer-handbook">Client handbook</Link>
              <CaregiverPortalLink className="text-xs font-medium text-stone-500 transition-colors hover:text-[#6A3E97]" testid="site-staff-login">Caregiver portal</CaregiverPortalLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
